import * as React from "react";
import { PFTOverreading, PFTResults } from './PFTAPITypes';
import { useFixedCommentsState } from "../../Core/FixedCommentsContext";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { FixedComment } from "webportalclient";
import { FixedCommentSetTypeEnum } from "../../misc/api/FixedComments/FixedCommentSetTypes";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

export interface SelectableFixedComment extends FixedComment {
    selected: boolean;
}

export interface TestFixedCommentsState {
    id: string;
    testNumber: number;
    isVisible: boolean;
    comments: SelectableFixedComment[];
}

export interface SessionFixedCommentState {
    isVisible: boolean;
    comments: SelectableFixedComment[];
}

export interface FixedCommentsState {
    isPageValidated: boolean;
    areAvailableFixedCommentsLoaded?: boolean;
    areFixedCommentsLoaded?: boolean;
    sessionFixedComments: SessionFixedCommentState;
    testFixedComments: TestFixedCommentsState[];
}

const initialFixedCommentsState = {
    isPageValidated: true,
    areAvailableFixedCommentsLoaded: null,
    areFixedCommentsLoaded: null,
    sessionFixedComments: {
        isVisible: false,
        comments: [],
    },
    testFixedComments: [],
}

export enum FixedCommentsActionTypes {
    ToggleSessionFixedCommentChecked = "ToggleSessionFixedCommentChecked",
    ToggleTestFixedCommentChecked = "ToggleTestFixedCommentChecked",
    ToggleTestFixedCommentsVisibility = "ToggleTestFixedCommentsVisibility",
    ToggleSessionFixedCommentsVisibility = "ToggleSessionFixedCommentsVisibility",
    LoadSessionFixedComments = "LoadSessionFixedComments",
    LoadTestFixedComments = "LoadTestFixedComments",
    LoadAnalysisSetOverread = "LoadAnalysisSetOverread",
    PageValidated = "PageValidated",
    LoadResults = "LoadResults",
    LoadedBothAvailableFixedComments = "LoadedBothAvailableFixedComments"
}

type Actions = ToggleSessionFixedCommentCheckedAction | ToggleTestFixedCommentCheckedAction | ToggleTestFixedCommentsVisibilityAction | ToggleSessionFixedCommentsVisibilityAction | LoadSessionFixedCommentsAction | LoadTestFixedCommentsAction | LoadTestFixedCommentsAction | LoadedBothAvailableFixedCommentsAction | LoadAnalysisSetOverreadAction | PageValidatedAction;

export class ToggleSessionFixedCommentCheckedAction {
    type: FixedCommentsActionTypes.ToggleSessionFixedCommentChecked;
    id: string;
    checked: boolean;

    static create(id: string, checked: boolean): ToggleSessionFixedCommentCheckedAction {
        return {
            type: FixedCommentsActionTypes.ToggleSessionFixedCommentChecked,
            id: id,
            checked: checked,
        };
    }
}

export class ToggleTestFixedCommentCheckedAction {
    type: FixedCommentsActionTypes.ToggleTestFixedCommentChecked;
    testId: string;
    commentId: string;
    checked: boolean;

    static create(testId: string, commentId: string, checked: boolean): ToggleTestFixedCommentCheckedAction {
        return {
            type: FixedCommentsActionTypes.ToggleTestFixedCommentChecked,
            testId: testId,
            commentId: commentId,
            checked: checked,
        };
    }
}

export class ToggleTestFixedCommentsVisibilityAction {
    type: FixedCommentsActionTypes.ToggleTestFixedCommentsVisibility;
    testId: string;

    static create(testId: string): ToggleTestFixedCommentsVisibilityAction {
        return {
            type: FixedCommentsActionTypes.ToggleTestFixedCommentsVisibility,
            testId: testId,
        };
    }
}

export class ToggleSessionFixedCommentsVisibilityAction {
    type: FixedCommentsActionTypes.ToggleSessionFixedCommentsVisibility;

    static create(): ToggleSessionFixedCommentsVisibilityAction {
        return {
            type: FixedCommentsActionTypes.ToggleSessionFixedCommentsVisibility,
        };
    }
}

export class LoadSessionFixedCommentsAction {
    type: FixedCommentsActionTypes.LoadSessionFixedComments;
    comments: FixedComment[];

    static create(comments: FixedComment[]): LoadSessionFixedCommentsAction {
        return {
            type: FixedCommentsActionTypes.LoadSessionFixedComments,
            comments: comments,
        };
    }
}

export class LoadTestFixedCommentsAction {
    type: FixedCommentsActionTypes.LoadTestFixedComments;
    comments: FixedComment[];

    static create(comments: FixedComment[]): LoadTestFixedCommentsAction {
        return {
            type: FixedCommentsActionTypes.LoadTestFixedComments,
            comments: comments,
        };
    }
}


export class LoadedBothAvailableFixedCommentsAction {
    type: FixedCommentsActionTypes.LoadedBothAvailableFixedComments;

    static create(): LoadedBothAvailableFixedCommentsAction {
        return {
            type: FixedCommentsActionTypes.LoadedBothAvailableFixedComments,
        }
    }
}

export class LoadAnalysisSetOverreadAction {
    type: FixedCommentsActionTypes.LoadAnalysisSetOverread;
    overreading: PFTOverreading;

    static create(overreading: PFTOverreading): LoadAnalysisSetOverreadAction {
        return {
            type: FixedCommentsActionTypes.LoadAnalysisSetOverread,
            overreading: overreading,
        };
    }
}

export class PageValidatedAction {
    type: FixedCommentsActionTypes.PageValidated;
    isValidated: boolean;

    static create(isValidated: boolean): PageValidatedAction {
        return {
            type: FixedCommentsActionTypes.PageValidated,
            isValidated: isValidated,
        };
    }
}

export const useFixedComments = (pFTResults: PFTResults, sessionTypeId: SessionTypes): [FixedCommentsState, React.Dispatch<any>] => {

    const globalFixedCommentsState = useFixedCommentsState();
    const sessionFixedComments = globalFixedCommentsState.session.get(`${sessionTypeId}-${FixedCommentSetTypeEnum.Overread}`);
    const testFixedComments = globalFixedCommentsState.test.get(`${sessionTypeId}-${FixedCommentSetTypeEnum.Overread}`);

    const { study } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const reducer = (state: FixedCommentsState, action: Actions): FixedCommentsState => {
        switch (action.type) {
            case FixedCommentsActionTypes.LoadAnalysisSetOverread:
                const newState: FixedCommentsState = {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        comments: state.sessionFixedComments.comments.map(c => {
                            if (action.overreading.Overreading.AnalysisSetFixedComments.map(sfc => sfc.Id).includes(c.id)) {
                                return {
                                    ...c,
                                    selected: true,
                                };
                            } else {
                                return c;
                            }
                        })
                    },
                    testFixedComments: state.testFixedComments.map(tfc => {
                        if (action.overreading.Overreading.Tests.map(t => t.TestId).includes(tfc.id)) {

                            const test = action.overreading.Overreading.Tests.find(x => x.TestId == tfc.id);
                            const testFixedComments = test.Overreading && test.Overreading.TestFixedComments ? test.Overreading.TestFixedComments : [];

                            return {
                                ...tfc,
                                comments: tfc.comments.map(c => {

                                    if (testFixedComments.length > 0 && testFixedComments.map(x => x.Id).includes(c.id)) {
                                        return {
                                            ...c,
                                            selected: true,
                                        };
                                    } else {
                                        return c;
                                    }
                                })
                            }
                        } else {
                            return tfc;
                        }
                    }),
                }

                return {
                    ...newState,
                    areFixedCommentsLoaded:
                        newState.sessionFixedComments.comments
                            .length > 0
                        && newState.testFixedComments.map(x => x.comments)
                            .flat()
                            .length > 0,
                }
            case FixedCommentsActionTypes.LoadSessionFixedComments:
                return {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        comments: action.comments.map(c => ({
                            ...c,
                            selected: false,
                        })),
                    },
                };
            case FixedCommentsActionTypes.LoadTestFixedComments:
                if (pFTResults === null) {
                    return state;
                }

                const fixedComments = pFTResults.TestResults.map(tr => ({
                    id: tr.TestId,
                    testNumber: tr.TestNumber,
                    isVisible: false,
                    comments: action.comments.map(c => ({
                        ...c,
                        selected: false,
                    })),
                }));

                return {
                    ...state,
                    testFixedComments: fixedComments,
                };
            case FixedCommentsActionTypes.ToggleSessionFixedCommentChecked:
                return {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        comments: state.sessionFixedComments.comments.map(c => {
                            if (c.id === action.id) {
                                return {
                                    ...c,
                                    selected: !c.selected,
                                };
                            } else {
                                return c;
                            }
                        })
                    }
                };
            case FixedCommentsActionTypes.ToggleTestFixedCommentChecked:
                return {
                    ...state,
                    testFixedComments: state.testFixedComments.map(tfc => {
                        if (tfc.id === action.testId) {
                            return {
                                ...tfc,
                                comments: tfc.comments.map(c => {
                                    if (c.id === action.commentId) {
                                        return {
                                            ...c,
                                            selected: !c.selected,
                                        };
                                    } else {
                                        return c;
                                    }
                                })
                            }
                        } else {
                            return tfc;
                        }
                    }),
                };
            case FixedCommentsActionTypes.ToggleTestFixedCommentsVisibility:
                return {
                    ...state,
                    testFixedComments: state.testFixedComments.map(t => {
                        if (t.id === action.testId) {
                            return {
                                ...t,
                                isVisible: !t.isVisible,
                            };
                        }
                        else return t;
                    }
                    ),
                }
            case FixedCommentsActionTypes.ToggleSessionFixedCommentsVisibility:
                return {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        isVisible: !state.sessionFixedComments.isVisible,
                    },
                };
            case FixedCommentsActionTypes.PageValidated:
                return {
                    ...state,
                    isPageValidated: action.isValidated,
                };
            case FixedCommentsActionTypes.LoadedBothAvailableFixedComments:
                return {
                    ...state,
                    areAvailableFixedCommentsLoaded: true,
                }
            default:
                throw new Error("Unexpected Fixed Comments action");
        }
    }

    const [state, dispatch] = React.useReducer(reducer, initialFixedCommentsState);

    React.useEffect(() => {
        const fetchComments = async () => {
            if (sessionFixedComments) {
                dispatch(LoadSessionFixedCommentsAction.create(sessionFixedComments.filter(sfc => !sfc.disabled)));
            }

            if (testFixedComments) {
                dispatch(LoadTestFixedCommentsAction.create(testFixedComments.filter(tfc => !tfc.disabled)));
            }

            if (sessionFixedComments && testFixedComments && pFTResults) {
                let analysisOverread: PFTOverreading;
                if (sessionTypeId === SessionTypes.Fvc) {
                    analysisOverread = await legacyClient.getAnalysisSetOverreading(pFTResults.AnalysisSetId);
                } else if (sessionTypeId === SessionTypes.Svc) {
                    analysisOverread = await legacyClient.getSvcAnalysisSetOverreading(pFTResults.AnalysisSetId);
                } else if (sessionTypeId === SessionTypes.Dlco) {
                    analysisOverread = await legacyClient.getDlcoAnalysisSetOverreading(pFTResults.AnalysisSetId);
                } else if (sessionTypeId === SessionTypes.LungVolumePleth) {
                    analysisOverread = await legacyClient.getLungVolumeAnalysisSetOverreading(pFTResults.AnalysisSetId);
                } else if (sessionTypeId === SessionTypes.LungVolumeHelium) {
                    analysisOverread = await legacyClient.getLungVolumeAnalysisSetOverreading(pFTResults.AnalysisSetId);
                } else if (sessionTypeId === SessionTypes.Mip) {
                    analysisOverread = await legacyClient.getMipAnalysisSetOverreading(pFTResults.AnalysisSetId);
                } else if (sessionTypeId === SessionTypes.Mep) {
                    analysisOverread = await legacyClient.getMepAnalysisSetOverreading(pFTResults.AnalysisSetId);
                } else if (sessionTypeId === SessionTypes.Snip) {
                    analysisOverread = await legacyClient.getSnipAnalysisSetOverreading(pFTResults.AnalysisSetId);
                }
                else if (sessionTypeId === SessionTypes.Oscillometry) {
                    analysisOverread = await legacyClient.getOscillometryAnalysisSetOverreading(pFTResults.AnalysisSetId);
                }

                dispatch(LoadAnalysisSetOverreadAction.create(analysisOverread));
            }

            dispatch(LoadedBothAvailableFixedCommentsAction.create());
        };

        fetchComments();
    }, [study.id, dispatch, pFTResults, sessionTypeId])

    return [state, dispatch];
}
